const MATCH_THRESHOLD = 0.6;
const MIN_VALID_SELFIE_SAMPLES = 2;

type FaceApiModule = typeof import('face-api.js');

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Falha ao carregar imagem'));
    img.src = src;
  });
}

export function evaluateFaceMatch(distances: number[]) {
  const validDistances = distances.filter(Number.isFinite);

  if (validDistances.length === 0) {
    throw new Error('no_face_in_selfie');
  }

  if (validDistances.length < MIN_VALID_SELFIE_SAMPLES) {
    throw new Error('insufficient_face_samples');
  }

  const sortedDistances = [...validDistances].sort(
    (left, right) => left - right,
  );
  const middleIndex = Math.floor(sortedDistances.length / 2);
  const robustDistance =
    sortedDistances.length % 2 === 0
      ? (sortedDistances[middleIndex - 1] + sortedDistances[middleIndex]) / 2
      : sortedDistances[middleIndex];

  return {
    match: robustDistance < MATCH_THRESHOLD,
    distance: robustDistance,
  };
}

export class FaceComparer {
  private faceapi: FaceApiModule | null = null;

  async initialize(): Promise<void> {
    const faceapi = await import('face-api.js');
    this.faceapi = faceapi;

    const modelUrl = '/models/face-api';
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl),
      faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl),
      faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl),
    ]);
  }

  async compare(
    profileBlobUrl: string,
    selfieDataUrls: string | string[],
  ): Promise<{ match: boolean; distance: number }> {
    if (!this.faceapi) {
      throw new Error('FaceComparer não foi inicializado');
    }

    const { faceapi } = this;
    const options = new faceapi.TinyFaceDetectorOptions({
      inputSize: 416,
      scoreThreshold: 0.3,
    });
    const selfieList = Array.isArray(selfieDataUrls)
      ? selfieDataUrls
      : [selfieDataUrls];

    const profileImg = await loadImage(profileBlobUrl);
    const profileDetection = await faceapi
      .detectSingleFace(profileImg, options)
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (!profileDetection) {
      throw new Error('no_face_in_profile');
    }

    const distances = await Promise.all(
      selfieList.map(async selfieDataUrl => {
        try {
          const selfieImg = await loadImage(selfieDataUrl);
          const selfieDetection = await faceapi
            .detectSingleFace(selfieImg, options)
            .withFaceLandmarks()
            .withFaceDescriptor();

          if (!selfieDetection) return Infinity;

          return faceapi.euclideanDistance(
            profileDetection.descriptor,
            selfieDetection.descriptor,
          );
        } catch {
          return Infinity;
        }
      }),
    );

    return evaluateFaceMatch(distances);
  }
}
